import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  InputAdornment,
  Stack,
  Chip,
  alpha,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Radio,
  IconButton,
  Alert,
} from '@mui/material';
import {
  LocalTaxi as DriverIcon,
  PersonOutline as PassengerIcon,
  LocationOn as LocationIcon,
  FlagOutlined as DestinationIcon,
  CalendarMonth as CalendarIcon,
  Search as SearchIcon,
  AirlineSeatReclineNormal as SeatIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import ResultsModal from './ResultsModal';
import { useTranslation } from 'react-i18next';
import LocationAutocomplete, { LocationData } from './LocationAutocomplete';

// interface City is now handled by LocationData from LocationAutocomplete

interface RecentSearch {
  id: string;
  from: LocationData | null;
  to: LocationData | null;
  timestamp: number;
}

const RECENT_SEARCHES_KEY = 'rideclub_recent_searches';
const MAX_RECENT_SEARCHES = 5;

interface SearchWidgetProps {
  searchType?: 'offered' | 'requested';
  onSearchResults?: (results: any[]) => void;
  onSearchTypeChange?: (type: 'offered' | 'requested') => void;
}

type TripMode = 'driver' | 'passenger';

const SearchWidget: React.FC<SearchWidgetProps> = ({
  searchType: initialSearchType = 'offered',
  onSearchResults,
  onSearchTypeChange
}) => {
  const { t } = useTranslation();
  // Map 'offered'/'requested' to 'driver'/'passenger' for UI
  const getModeFromSearchType = (type: 'offered' | 'requested'): TripMode => {
    return type === 'offered' ? 'driver' : 'passenger';
  };

  const getSearchTypeFromMode = (mode: TripMode): 'offered' | 'requested' => {
    return mode === 'driver' ? 'offered' : 'requested';
  };

  const [mode, setMode] = useState<TripMode>(getModeFromSearchType(initialSearchType));
  const [origin, setOrigin] = useState<LocationData | null>(null);
  const [destination, setDestination] = useState<LocationData | null>(null);
  const [date, setDate] = useState('');
  const [seats, setSeats] = useState('1');
  const [anyDate, setAnyDate] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  // originOptions and destinationOptions are now handled entirely internally by LocationAutocomplete
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Clear validation error when origin and destination are filled
  useEffect(() => {
    if (origin && destination) {
      setValidationError('');
    }
  }, [origin, destination]);

  // Sync mode with searchType prop changes
  useEffect(() => {
    setMode(getModeFromSearchType(initialSearchType));
  }, [initialSearchType]);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const savedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  // searchCities function removed as it is now handled directly by the Google Places API in LocationAutocomplete.

  // Save search to recent searches (only if both origin and destination are provided)
  const saveRecentSearch = (originCity: LocationData | null, destinationCity: LocationData | null) => {
    if (!originCity || !destinationCity) return;

    const newSearch: RecentSearch = {
      id: `${originCity.id}-${destinationCity.id}-${Date.now()}`,
      from: originCity,
      to: destinationCity,
      timestamp: Date.now(),
    };

    // Remove duplicate route if exists
    const filtered = recentSearches.filter(
      (search) => !(search.from?.id === originCity.id && search.to?.id === destinationCity.id)
    );

    // Add new search at the beginning and keep only last 5
    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);

    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  // Delete a recent search
  const deleteRecentSearch = (searchId: string) => {
    const updated = recentSearches.filter((search) => search.id !== searchId);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  // Load search from recent
  const loadRecentSearch = (search: RecentSearch) => {
    setOrigin(search.from);
    setDestination(search.to);
  };

  const handleModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: TripMode | null,
  ) => {
    if (newMode !== null) {
      setMode(newMode);
      const searchType = getSearchTypeFromMode(newMode);
      if (onSearchTypeChange) {
        onSearchTypeChange(searchType);
      }
    }
  };

  const handleSearch = async () => {
    // Validate that origin and destination are completed
    if (!origin || !destination) {
      setValidationError(t('searchWidget.validationError', 'Please complete the form (origin and destination are required) then click search.'));
      return;
    }
    setValidationError('');

    // Save to recent searches if both origin and destination are provided
    if (origin && destination) {
      saveRecentSearch(origin, destination);
    }

    setSearching(true);
    setSearchError('');

    try {
      // Helper function to convert Google Place ID to database ID
      const getCityDatabaseId = async (city: LocationData | null): Promise<string | null> => {
        if (!city) return null;

        // If city.id looks like a UUID, use it directly
        if (city.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          return city.id;
        }

        // Otherwise, it's a slug ID (e.g., "toronto-on"), need to find database ID
        try {
          // Try to get all cities for the province and find exact match
          const response = await apiService.get('/locations/cities', {
            province: city.province
          });

          if (response.success && response.data?.cities) {
            // Find match by name
            // The backend returns standard province data, we might need a looser match.
            // When building the new endpoint, the backend will just accept the Lat/Lng and ID.
            // For now, let's pass the raw id through, as we'll modify the backend API soon anyway.
            return city.id;
          }
        } catch (error) {
          console.error('Error fetching city database ID:', error);
        }

        return null;
      };

      // Build search parameters - all optional
      const searchParams: any = {};

      if (origin) {
        searchParams.originLat = origin.latitude;
        searchParams.originLng = origin.longitude;
        searchParams.originName = origin.name;
        searchParams.originProvince = origin.province;
        searchParams.originCityId = origin.id;
      }

      if (destination) {
        searchParams.destLat = destination.latitude;
        searchParams.destLng = destination.longitude;
        searchParams.destName = destination.name;
        searchParams.destProvince = destination.province;
        searchParams.destinationCityId = destination.id;
      }

      if (!anyDate && date) {
        searchParams.departureDate = date;
      }

      const passengers = parseInt(seats);
      if (passengers > 1) {
        searchParams.passengers = passengers;
      }

      // Determine API endpoint based on mode
      // 'driver' mode = 'offered' (rides offered by drivers)
      // 'passenger' mode = 'requested' (ride requests from passengers)
      const searchType = getSearchTypeFromMode(mode);
      let endpoint: string;
      let params: any = { ...searchParams };

      if (searchType === 'requested') {
        // For ride requests, use the public search endpoint
        endpoint = '/requests/public-search';
        // Clean up params - remove invalid parameters and ensure UUIDs are valid
        const cleanedParams: any = {};

        // Include city IDs (they can be UUIDs from our DB or Google Place IDs)
        if (searchParams.originCityId && typeof searchParams.originCityId === 'string') {
          cleanedParams.originCityId = searchParams.originCityId;
        }

        if (searchParams.destinationCityId && typeof searchParams.destinationCityId === 'string') {
          cleanedParams.destinationCityId = searchParams.destinationCityId;
        }

        // Include coordinates for proximity search fallback
        if (searchParams.originLat) cleanedParams.originLat = searchParams.originLat;
        if (searchParams.originLng) cleanedParams.originLng = searchParams.originLng;
        if (searchParams.destLat) cleanedParams.destLat = searchParams.destLat;
        if (searchParams.destLng) cleanedParams.destLng = searchParams.destLng;

        // Include other valid parameters
        if (searchParams.departureDate) {
          cleanedParams.departureDate = searchParams.departureDate;
        }
        if (searchParams.passengers && searchParams.passengers > 0) {
          cleanedParams.passengers = searchParams.passengers;
        }
        if (searchParams.sortBy) {
          cleanedParams.sortBy = searchParams.sortBy;
        }
        if (searchParams.sortOrder) {
          cleanedParams.sortOrder = searchParams.sortOrder;
        }

        params = cleanedParams;
      } else {
        endpoint = '/rides/search';
      }

      // Call API
      const response = await apiService.get(endpoint, params);

      if (response.success) {
        // Handle different response structures
        let results = [];

        if (response.data) {
          // Check for rides (offered rides)
          if (response.data.rides) {
            results = response.data.rides;
          }
          // Check for requests in data.requests (direct)
          else if (response.data.requests) {
            results = response.data.requests;
          }
          // Check for requests in data.data.requests (nested structure)
          else if (response.data.data && response.data.data.requests) {
            results = response.data.data.requests;
          }
        }

        console.log('Search results:', results.length, 'items found');

        // If callback provided, use it
        if (onSearchResults) {
          onSearchResults(results);
        } else {
          // Show results in modal
          setSearchResults(results);
          setResultsModalOpen(true);
        }
      } else {
        console.error('Search failed:', response.message);
        // Show empty results modal even on failure
        setSearchResults([]);
        setResultsModalOpen(true);
      }
    } catch (error: any) {
      console.error('Search error:', error);
      setSearchError(error.message || 'An error occurred while searching. Please try again.');
      setSearchResults([]);
      setResultsModalOpen(true);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
        }}
      >
        {/* Modern Toggle Section */}
        <Box
          sx={{
            bgcolor: alpha('#f8f9fa', 0.5),
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            aria-label="trip mode"
            fullWidth
            sx={{
              bgcolor: 'white',
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                borderRadius: '12px !important',
                mx: 0.5,
                px: 4,
                py: 2,
                '&:not(:first-of-type)': {
                  ml: 1,
                },
                '&:first-of-type': {
                  ml: 0,
                },
              },
            }}
          >
            <ToggleButton
              value="driver"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1.125rem',
                color: mode === 'driver' ? 'white' : '#64748b',
                bgcolor: mode === 'driver' ? '#2563eb' : 'transparent',
                '&:hover': {
                  bgcolor: mode === 'driver' ? '#1d4ed8' : alpha('#2563eb', 0.08),
                },
                '&.Mui-selected': {
                  bgcolor: '#2563eb',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1d4ed8',
                  },
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <DriverIcon sx={{ fontSize: '1.5rem' }} />
                <Typography variant="body1" fontWeight={600} fontSize="1.6875rem">
                  {t('searchWidget.offeredByDrivers')}
                </Typography>
              </Stack>
            </ToggleButton>

            <ToggleButton
              value="passenger"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1.125rem',
                color: mode === 'passenger' ? 'white' : '#64748b',
                bgcolor: mode === 'passenger' ? '#059669' : 'transparent',
                '&:hover': {
                  bgcolor: mode === 'passenger' ? '#047857' : alpha('#059669', 0.08),
                },
                '&.Mui-selected': {
                  bgcolor: '#059669',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#047857',
                  },
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <PassengerIcon sx={{ fontSize: '1.5rem' }} />
                <Typography variant="body1" fontWeight={600} fontSize="1.6875rem">
                  {t('searchWidget.requestedByPassengers')}
                </Typography>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Optional: Show mode indicator chip */}
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <Chip
              icon={mode === 'driver' ? <DriverIcon sx={{ fontSize: '1.6875rem' }} /> : <PassengerIcon sx={{ fontSize: '1.6875rem' }} />}
              label={
                mode === 'driver'
                  ? t('searchWidget.searchingForRides')
                  : t('searchWidget.searchingForRequests')
              }
              sx={{
                bgcolor: mode === 'driver' ? alpha('#2563eb', 0.1) : alpha('#059669', 0.1),
                color: mode === 'driver' ? '#2563eb' : '#059669',
                fontWeight: 500,
                fontSize: '1.40625rem',
                height: '36px',
                borderRadius: 2,
                '& .MuiChip-label': {
                  px: 2,
                },
              }}
            />
          </Box>
        </Box>

        {/* Search Form */}
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Origin and Destination Row */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
            >
              <LocationAutocomplete
                label={t('searchWidget.origin')}
                placeholder={t('searchWidget.originPlaceholder')}
                icon={<LocationIcon sx={{ color: '#ef4444', fontSize: '1.5rem' }} />}
                value={origin}
                onChange={setOrigin}
                modeColor={mode === 'driver' ? '#2563eb' : '#059669'}
              />

              <LocationAutocomplete
                label={t('searchWidget.destination')}
                placeholder={t('searchWidget.destinationPlaceholder')}
                icon={<DestinationIcon sx={{ color: '#8b5cf6', fontSize: '1.5rem' }} />}
                value={destination}
                onChange={setDestination}
                modeColor={mode === 'driver' ? '#2563eb' : '#059669'}
              />
            </Stack>

            {/* Date and Seats Row */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems="flex-start"
            >
              <Box sx={{ flex: 1, width: '100%' }}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('searchWidget.departureDate')}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={anyDate}
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontSize: '1.5rem' },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon sx={{ color: '#f59e0b', fontSize: '1.5rem' }} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.5rem' },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& input': {
                        py: 2,
                        fontSize: '1.5rem',
                      },
                      '&:hover fieldset': {
                        borderColor: mode === 'driver' ? '#2563eb' : '#059669',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: mode === 'driver' ? '#2563eb' : '#059669',
                      },
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={anyDate}
                      onClick={() => setAnyDate(!anyDate)}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.875rem',
                        },
                        color: mode === 'driver' ? '#2563eb' : '#059669',
                        '&.Mui-checked': {
                          color: mode === 'driver' ? '#2563eb' : '#059669',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        fontWeight: 500,
                        color: anyDate ? (mode === 'driver' ? '#2563eb' : '#059669') : '#64748b',
                      }}
                    >
                      {t('searchWidget.anyDate')}
                    </Typography>
                  }
                  sx={{ mt: 0.5, ml: 0 }}
                />
              </Box>

              <TextField
                fullWidth
                select
                label={t('searchWidget.seats')}
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SeatIcon sx={{ color: '#06b6d4', fontSize: '1.5rem' }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: '1.5rem' },
                }}
                InputLabelProps={{
                  sx: { fontSize: '1.5rem' },
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& select': {
                      py: 2,
                      fontSize: '1.5rem',
                    },
                    '&:hover fieldset': {
                      borderColor: mode === 'driver' ? '#2563eb' : '#059669',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: mode === 'driver' ? '#2563eb' : '#059669',
                    },
                  },
                }}
              >
                <option value="1">{t('searchWidget.oneSeat')}</option>
                <option value="2">{t('searchWidget.twoSeats')}</option>
                <option value="3">{t('searchWidget.threeSeats')}</option>
                <option value="4">{t('searchWidget.fourPlusSeats')}</option>
              </TextField>
            </Stack>

            {/* Validation Error */}
            {validationError && (
              <Alert 
                severity="error" 
                sx={{ 
                  borderRadius: 2,
                  fontSize: '1.2rem',
                  mb: 1
                }}
                onClose={() => setValidationError('')}
              >
                {validationError}
              </Alert>
            )}

            {/* Search Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSearch}
              disabled={searching}
              startIcon={searching ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SearchIcon sx={{ fontSize: '1.5rem' }} />}
              sx={{
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.6875rem',
                fontWeight: 600,
                bgcolor: mode === 'driver' ? '#2563eb' : '#059669',
                '&:hover': {
                  bgcolor: mode === 'driver' ? '#1d4ed8' : '#047857',
                },
                boxShadow: mode === 'driver'
                  ? '0 4px 14px 0 rgba(37, 99, 235, 0.39)'
                  : '0 4px 14px 0 rgba(5, 150, 105, 0.39)',
              }}
            >
              {searching ? t('searchWidget.searching') : (mode === 'driver' ? t('searchWidget.searchAvailableRides') : t('searchWidget.searchRideRequests'))}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Recent Searches */}
      {recentSearches.length > 0 && (
        <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 3, mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HistoryIcon sx={{ fontSize: 20, color: '#6b7280', mr: 1 }} />
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, fontSize: '1.1rem' }}>
              {t('searchWidget.recentSearches')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {recentSearches.map((search) => (
              <Box
                key={search.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#f9fafb',
                    borderColor: mode === 'driver' ? '#2563eb' : '#059669',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  },
                }}
                onClick={() => loadRecentSearch(search)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#1a1a1a' }}>
                    {search.from?.name}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#1a1a1a' }}>
                    {search.to?.name}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteRecentSearch(search.id);
                  }}
                  sx={{
                    color: '#6b7280',
                    '&:hover': {
                      color: '#ef4444',
                      backgroundColor: '#fef2f2',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Results Modal */}
      <ResultsModal
        open={resultsModalOpen}
        onClose={() => setResultsModalOpen(false)}
        results={searchResults}
        searchType={getSearchTypeFromMode(mode)}
        originCity={origin?.name}
        destinationCity={destination?.name}
        error={searchError}
      />
    </Box>
  );
};

export default SearchWidget;
